import { useEffect, useRef } from 'react'
import { Icon } from '@carrent/ui'

/**
 * Боковая панель консоли.
 *
 * ── Зачем ─────────────────────────────────────────────────────────────
 *
 * Просмотр десяти машин подряд был десятью переходами туда-обратно:
 * список — карточка — назад — список, и каждый раз заново прокрутка
 * и фильтры. Панель показывает главное, не теряя список под собой.
 *
 * ── Почему это примитив, а не разметка внутри страницы ────────────────
 *
 * Панель обязана вести себя как модальный слой, а это не про внешний
 * вид. Escape закрывает; фокус уходит внутрь при открытии и
 * возвращается ровно на тот элемент, откуда открыли; Tab не убегает
 * в список за спиной. Каждый из этих пунктов забывается поодиночке,
 * и написанная во второй раз панель почти наверняка потеряет один.
 *
 * ── Про возврат фокуса ────────────────────────────────────────────────
 *
 * Он важнее, чем кажется: без него закрытие панели, открытой с
 * тридцатой строки таблицы, бросает клавиатурного пользователя
 * в начало документа. Возвращать надо на сам элемент, а не «в
 * таблицу» — только тогда следующий Tab продолжает с того же места.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Drawer({ open, title, subtitle, onClose, children, footer }) {
  const panelRef = useRef(null)
  const restoreTo = useRef(null)

  useEffect(() => {
    if (!open) return

    restoreTo.current = document.activeElement
    const panel = panelRef.current
    // Фокус на саму панель, а не на первую кнопку: иначе screen reader
    // начинает читать с середины, пропуская заголовок.
    panel?.focus()

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const items = [...(panel?.querySelectorAll(FOCUSABLE) || [])]
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Элемент мог исчезнуть вместе с перерисовкой списка — тогда
      // возвращать некуда, и это не повод падать.
      const target = restoreTo.current
      if (target && document.contains(target)) target.focus()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="drawer">
      {/* Подложка не получает роль кнопки: клавиатурный путь закрытия —
          Escape и кнопка «Закрыть», дублировать его в невидимом
          элементе значит добавить в обход лишнюю остановку. */}
      <div className="drawer__scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className="drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="drawer__head">
          <div className="drawer__titles">
            <p className="drawer__title">{title}</p>
            {subtitle && <p className="drawer__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Закрыть панель">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="drawer__body">{children}</div>

        {footer && <footer className="drawer__foot">{footer}</footer>}
      </aside>
    </div>
  )
}
